import type {Conversation} from '@stripe-internal/conversations-client';

import type {AppFeatureFlags} from '../../core/types';
import type {ConversationLifecycleEvent} from './ThreadsConversationClient';
import {isDotComConversation} from '../../core/lib/util/conversationUtils';
import {messages} from '../../core/lib/SupportConversationsClient';

/**
 * Builds the handler that writes conversation lifecycle events (chat starting,
 * chat ended) into the conversation log as meta events. It never records
 * lifecycle facts.
 */
export function buildConversationLifecycleEventLogHandler(
  conversation: Conversation,
  getFeatureFlags: () => AppFeatureFlags,
) {
  return (event: ConversationLifecycleEvent): void => {
    const featureFlags = getFeatureFlags();
    const newDesign =
      featureFlags.universalHelpUseNewDesign &&
      featureFlags.enableUniversalHelp;
    switch (event.name) {
      case 'chat-starting': {
        let message = messages.transitionToAgent;
        if (newDesign) {
          message = messages.transitionToAgentV2;
        } else if (isDotComConversation(conversation)) {
          message = messages.transitionToAgentDotCom;
        }
        conversation.log.appendMetaEvent({
          id: 'transition-to-agent',
          message,
          ...(newDesign ? {style: 'caption' as const} : {}),
        });
        break;
      }
      case 'chat-ended': {
        const suppressed = conversation.context.supressChatEndedNotice;
        if (suppressed || newDesign) {
          break;
        }
        conversation.log.appendMetaEvent({
          id: 'chat-ended-notice',
          message: messages.chatEndedMessage,
        });
        break;
      }
      default:
        break;
    }
  };
}
